/* WP-10 core primitives (§14.4.6): buttons, links, section headers,
 * fact rows, step numbers, banners, empty states. Journal-styled. */
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { CheckIcon, ChevronIcon } from '../icons';
import { cn } from '../utils/cn';

export function PrimaryButton({
  loading,
  icon,
  children,
  className,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  icon?: ReactNode;
}) {
  return (
    <button
      type="button"
      className={cn('hp-btn hp-btn--primary', className)}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <span className="hp-btn__spinner" aria-hidden /> : icon}
      <span>{children}</span>
    </button>
  );
}

export function SecondaryButton({
  loading,
  icon,
  danger,
  children,
  className,
  disabled,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  loading?: boolean;
  icon?: ReactNode;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      className={cn(
        'hp-btn',
        danger ? 'hp-btn--danger' : 'hp-btn--secondary',
        className,
      )}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <span className="hp-btn__spinner" aria-hidden /> : icon}
      <span>{children}</span>
    </button>
  );
}

export function TextLink({
  children,
  className,
  tone,
  chevron = true,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  tone?: 'danger' | 'muted';
  chevron?: boolean;
}) {
  return (
    <button
      type="button"
      className={cn(
        'hp-textlink',
        tone === 'danger' && 'hp-textlink--danger',
        tone === 'muted' && 'hp-textlink--muted',
        className,
      )}
      {...rest}
    >
      {children}
      {chevron ? <ChevronIcon size={14} /> : null}
    </button>
  );
}

export function SectionHeader({
  title,
  count,
  action,
  level = 2,
}: {
  title: ReactNode;
  count?: ReactNode;
  action?: ReactNode;
  level?: 1 | 2;
}) {
  const Tag = level === 1 ? 'h1' : 'h2';
  return (
    <div className="hp-section__head">
      <Tag className="hp-title">{title}</Tag>
      {count != null ? <span className="hp-section__count">{count}</span> : null}
      {action}
    </div>
  );
}

export function FactRow({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <div className="hp-fact">
      {icon}
      <span>{children}</span>
    </div>
  );
}

export function StepNumber({
  n,
  label,
  done,
}: {
  n: number;
  label: ReactNode;
  done?: boolean;
}) {
  return (
    <div className={cn('hp-step', done && 'hp-step--done')}>
      <span className="hp-step__num" aria-hidden>
        {done ? <CheckIcon size={14} /> : n}
      </span>
      <span className="hp-label">{label}</span>
    </div>
  );
}

export function Banner({
  style = 'info',
  icon,
  children,
  link,
  onDismiss,
}: {
  style?: 'info' | 'notice';
  icon?: ReactNode;
  children: ReactNode;
  link?: { label: string; onClick: () => void } | null;
  onDismiss?: () => void;
}) {
  return (
    <div className={cn('hp-banner', style === 'notice' && 'hp-banner--notice')} role="note">
      {icon}
      <div className="hp-banner__body">
        {children}
        {link ? (
          <div>
            <button type="button" className="hp-textlink" onClick={link.onClick}>
              {link.label}
            </button>
          </div>
        ) : null}
      </div>
      {onDismiss ? (
        <button type="button" className="hp-banner__dismiss" onClick={onDismiss} aria-label="Dismiss">
          ×
        </button>
      ) : null}
    </div>
  );
}

export function EmptyState({
  illustration,
  sentence,
  action,
}: {
  illustration?: ReactNode;
  sentence: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="hp-empty">
      {illustration}
      <p className="hp-empty__sentence">{sentence}</p>
      {action}
    </div>
  );
}
